import { initCertificateSelect2, initCountrySelect2 } from '../select2';

const ENTRY_AUTH_TOKEN = $('meta[name="csrf-token"]').attr('content');
const MAX_PROPOSALS = 3;
const AUTOSAVE_DEBOUNCE_MS = 400;

window.EntryDealPricingRequestProposals = {
    debounceTimers: {},

    init() {
        this.dealPricingRequestId = $('#pricing-proposal-entry-page').data('deal-pricing-request-id');
        this.companyDetailId = $('#pricing-proposal-entry-page').data('company-detail-id');
        this.bindPreventEnterSubmit();
        this.bindContextFieldAutosave();
        this.bindWarehouseLocationAutosave();
        this.bindClientExperienceAutosave();
        this.bindCompanyDetailFieldAutosave();
        this.bindProposalFieldAutosave();
        this.bindSkuRowFieldAutosave();
        this.bindPriceAutosave();
        this.bindAddProposal();
        this.bindRemoveProposal();
        this.bindPrimaryToggle();
        this.bindPackagingRequirementsToggle();
        this.bindTermsAcceptToggle();
        this.bindLogisticsAcceptToggle();
        this.bindSelectWithOtherToggle();
        this.bindTabSwitching();
        this.bindProposalNameLiveUpdate();
        this.bindSaveAsDraft();
        this.bindSubmitPricing();
        this.initDropZone();
        this.bindRemoveAttachment();
        this.updateAddProposalButtonState();
    },

    debounce(key, fn, wait = AUTOSAVE_DEBOUNCE_MS) {
        const existing = this.debounceTimers[key];
        if (existing) clearTimeout(existing.timer);

        const timer = setTimeout(() => {
            delete this.debounceTimers[key];
            fn();
        }, wait);
        this.debounceTimers[key] = { timer, fn };
    },

    // Immediately runs (rather than discards) any autosave still waiting out its debounce delay, so a
    // field edited right before Save Draft/Submit Pricing isn't silently lost. Returns a promise that
    // resolves once every flushed save's request has completed.
    flushPendingAutosaves() {
        const pending = Object.values(this.debounceTimers).map(({ timer, fn }) => {
            clearTimeout(timer);
            return fn();
        });
        this.debounceTimers = {};
        return $.when(...pending);
    },

    // --- Supplier context fields + global Additional Information (existing update endpoint) ---
    bindContextFieldAutosave() {
        const self = this;
        $('#pricing-proposal-entry-form').on('blur change', '.autosave-field', function () {
            const field = $(this);
            self.debounce(`context-${field.attr('name')}`, () => self.autosaveContextField(field));
        });
    },

    autosaveContextField(field) {
        // field.attr('name') is already the fully-bracketed Rails form field name
        // (e.g. "deal_pricing_request[warehouse_logistics_locations]") since these fields are
        // rendered via the form builder — do not re-wrap it, or the param key doubles up and the
        // controller silently never sees the value.
        const data = {};
        data[field.attr('name')] = field.val();

        return $.ajax({
            url: `/deal_pricing_requests/${this.dealPricingRequestId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- Warehouse locations (specs/011-pricing-request-locations US3, research.md Decision 5) —
    // a repeatable list rather than a single field, so the whole current set is serialized and
    // PATCHed together on any row edit or add/remove click, instead of one field at a time. ---
    bindWarehouseLocationAutosave() {
        const self = this;
        const list = document.getElementById('warehouse-location-list');
        if (!list) return;

        $('#pricing-proposal-entry-form').on('blur change', '.autosave-warehouse-location-field', function () {
            self.debounce('context-warehouse_locations', () => self.autosaveWarehouseLocations());
        });

        const addBtn = document.getElementById('add-warehouse-location-btn');
        if (addBtn) {
            addBtn.addEventListener('click', function () {
                const row = document.createElement('div');
                row.className = 'd-flex flex-row align-items-center mb-1 warehouse-location-row';
                row.innerHTML = '<input class="form-control mr-2 autosave-warehouse-location-field" type="text" name="deal_pricing_request[warehouse_locations][]"> ' +
                    '<button type="button" class="btn btn-link text-danger remove-warehouse-location"><i class="fas fa-trash"></i></button>';
                list.appendChild(row);
                self.debounce('context-warehouse_locations', () => self.autosaveWarehouseLocations());
            });
        }

        list.addEventListener('click', function (e) {
            const removeBtn = e.target.closest('.remove-warehouse-location');
            if (removeBtn) {
                removeBtn.closest('.warehouse-location-row').remove();
                self.debounce('context-warehouse_locations', () => self.autosaveWarehouseLocations());
            }
        });
    },

    autosaveWarehouseLocations() {
        const data = {};
        data['deal_pricing_request[warehouse_locations]'] = Array.from(
            document.querySelectorAll('#warehouse-location-list input[name="deal_pricing_request[warehouse_locations][]"]')
        ).map((el) => el.value).filter(Boolean);

        return $.ajax({
            url: `/deal_pricing_requests/${this.dealPricingRequestId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- Client experience: clients + countries supplied (specs/014-client-countries-supplied) —
    // like warehouse locations above, the whole current set of rows is serialized and PATCHed
    // together on any row edit or add/remove click. Each row is two independent selects (a
    // single-value recognized-or-free-text client name, and a multi-value countries-supplied
    // list); a new row's option list is copied from the first existing row rather than duplicated
    // in JS, since the server always renders at least one row (research.md Decision 2/4/5). ---
    bindClientExperienceAutosave() {
        const self = this;
        const list = document.getElementById('client-experience-list');
        if (!list) return;

        $('#pricing-proposal-entry-form').on('change', '.autosave-client-experience-field', function () {
            self.debounce('context-client_rows', () => self.autosaveClientExperience());
        });

        const addBtn = document.getElementById('add-client-experience-btn');
        if (addBtn) {
            addBtn.addEventListener('click', function () {
                const templateNameSelect = list.querySelector('.multi-select2-supplier-clients');
                const templateCountrySelect = list.querySelector('.multi-select2-client-countries');
                if (!templateNameSelect || !templateCountrySelect) return;

                const index = list.querySelectorAll('.client-experience-row').length;
                const row = document.createElement('div');
                row.className = 'd-flex flex-row align-items-start mb-1 client-experience-row';

                const nameSelect = document.createElement('select');
                nameSelect.className = 'form-control mr-2 autosave-client-experience-field multi-select2-supplier-clients';
                nameSelect.name = `deal_pricing_request[client_rows][${index}][name]`;
                nameSelect.innerHTML = templateNameSelect.innerHTML;
                nameSelect.value = '';

                const countrySelect = document.createElement('select');
                countrySelect.className = 'form-control mr-2 autosave-client-experience-field multi-select2-client-countries';
                countrySelect.name = `deal_pricing_request[client_rows][${index}][geography_ids][]`;
                countrySelect.multiple = true;
                countrySelect.setAttribute('data-placeholder', templateCountrySelect.getAttribute('data-placeholder') || '');
                countrySelect.innerHTML = templateCountrySelect.innerHTML;
                Array.from(countrySelect.options).forEach((option) => { option.selected = false; });

                const removeBtn = document.createElement('button');
                removeBtn.type = 'button';
                removeBtn.className = 'btn btn-link text-danger remove-client-experience-row';
                removeBtn.innerHTML = '<i class="fas fa-trash"></i>';

                row.appendChild(nameSelect);
                row.appendChild(countrySelect);
                row.appendChild(removeBtn);
                list.appendChild(row);

                initCertificateSelect2(nameSelect);
                initCountrySelect2(countrySelect);

                self.debounce('context-client_rows', () => self.autosaveClientExperience());
            });
        }

        list.addEventListener('click', function (e) {
            const removeBtn = e.target.closest('.remove-client-experience-row');
            if (removeBtn) {
                removeBtn.closest('.client-experience-row').remove();
                self.debounce('context-client_rows', () => self.autosaveClientExperience());
            }
        });
    },

    autosaveClientExperience() {
        const rows = Array.from(document.querySelectorAll('#client-experience-list .client-experience-row'))
            .map((row) => {
                const nameSelect = row.querySelector('.multi-select2-supplier-clients');
                const countrySelect = row.querySelector('.multi-select2-client-countries');
                return {
                    name: nameSelect ? nameSelect.value : '',
                    geography_ids: countrySelect ? Array.from(countrySelect.selectedOptions).map((o) => o.value) : []
                };
            })
            .filter((row) => row.name);

        const data = {};
        data['deal_pricing_request[client_rows]'] = rows;

        return $.ajax({
            url: `/deal_pricing_requests/${this.dealPricingRequestId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- Company Information fields (exact turnover/employees/inception — belong to CompanyDetail,
    // not DealPricingRequest, so they autosave to a different resource/endpoint than the context
    // fields above; specs/010-pricing-comparison-enhancements US1, research.md Decision 3) ---
    bindCompanyDetailFieldAutosave() {
        const self = this;
        $('#pricing-proposal-entry-form').on('blur change', '.autosave-company-detail-field', function () {
            const field = $(this);
            self.debounce(`company-detail-${field.attr('name')}`, () => self.autosaveCompanyDetailField(field));
        });
    },

    autosaveCompanyDetailField(field) {
        if (!this.companyDetailId) return;

        // CompanyDetailsController#modify_company_detail_params permits these as top-level params
        // (matching how the existing address fields on this same endpoint already work), NOT nested
        // under a `company_detail[...]` key — field.attr('name') must stay unwrapped (e.g.
        // "kyb_yearly_turnover"), or params.permit(:kyb_yearly_turnover, ...) finds nothing and the
        // update silently no-ops.
        const data = {};
        data[field.attr('name')] = field.val();

        return $.ajax({
            url: `/company_details/${this.companyDetailId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- Proposal-level fields (name, description, rebate_conditions, pallet_type, pallet_height_cm, master_carton_weight_kg) ---
    bindProposalFieldAutosave() {
        const self = this;
        $('#pricing-proposals-container').on('blur change', '.autosave-proposal-field', function () {
            const field = $(this);
            const card = field.closest('.pricing-proposal-card');
            const proposalId = card.data('proposal-id');
            if (!proposalId) return;

            self.debounce(`proposal-${proposalId}-${field.attr('name')}`, () => self.autosaveProposalField(proposalId, field));
        });
    },

    autosaveProposalField(proposalId, field) {
        const data = {};
        data[field.attr('name')] = field.val();

        return $.ajax({
            url: `/deal_pricing_request_proposals/${proposalId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- SKU-row-level fields (MOQ, note) — apply to the whole row, independent of incoterm ---
    bindSkuRowFieldAutosave() {
        const self = this;
        $('#pricing-proposals-container').on('blur change', '.autosave-proposal-sku-field', function () {
            const field = $(this);
            const row = field.closest('.pricing-sku-row');
            const proposalSkuId = row.data('proposal-sku-id');
            if (!proposalSkuId) return;

            self.debounce(`proposal-sku-${proposalSkuId}-${field.attr('name')}`, () => self.autosaveSkuRowField(proposalSkuId, field));
        });
    },

    autosaveSkuRowField(proposalSkuId, field) {
        const data = {};
        data[field.attr('name')] = field.val();

        return $.ajax({
            url: `/deal_pricing_request_proposal_skus/${proposalSkuId}`,
            method: 'PATCH',
            data,
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    },

    // --- SKU x incoterm pricing grid ---
    bindPriceAutosave() {
        const self = this;
        $('#pricing-proposals-container').on('blur change', '.autosave-price-field', function () {
            const field = $(this);
            const card = field.closest('.pricing-proposal-card');
            const proposalId = card.data('proposal-id');
            if (!proposalId) return;

            const skuId = field.closest('.pricing-sku-row').data('sku-id');
            const key = `price-${proposalId}-${skuId}-${field.data('incoterm-id')}`;
            self.debounce(key, () => self.autosavePrice(proposalId, field));
        });
    },

    autosavePrice(proposalId, field) {
        const priceId = field.data('price-id');
        const payload = {
            deal_pricing_request_sku_id: field.closest('.pricing-sku-row').data('sku-id'),
            incoterm_id: field.data('incoterm-id'),
            unit_price: field.val()
        };

        const request = priceId
            ? $.ajax({
                url: `/deal_pricing_request_proposal_sku_prices/${priceId}`,
                method: 'PATCH',
                data: payload,
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            })
            : $.ajax({
                url: `/deal_pricing_request_proposals/${proposalId}/deal_pricing_request_proposal_sku_prices`,
                method: 'POST',
                data: payload,
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            });

        return request.done((response) => {
            if (response && response.id) {
                field.data('price-id', response.id);
                field.attr('data-price-id', response.id);
            }
        });
    },

    // --- Tab switching between proposals ---
    bindTabSwitching() {
        $('#pricing-proposals-container').on('click', '.pricing-proposal-tab', function (event) {
            event.preventDefault();
            EntryDealPricingRequestProposals.activateTab($(this).data('proposal-id'));
        });
    },

    activateTab(proposalId) {
        $('#pricing-proposals-container .pricing-proposal-tab').removeClass('active');
        $(`#pricing-proposals-container .pricing-proposal-tab[data-proposal-id="${proposalId}"]`).addClass('active');
        $('#pricing-proposals-container .pricing-proposal-pane').addClass('d-none');
        $(`#pricing-proposals-container .pricing-proposal-pane[data-proposal-id="${proposalId}"]`).removeClass('d-none');
    },

    // --- Keep each tab's label in sync with the proposal name as the supplier types it ---
    bindProposalNameLiveUpdate() {
        $('#pricing-proposals-container').on('input', '.pricing-proposal-pane input[name="name"]', function () {
            const pane = $(this).closest('.pricing-proposal-pane');
            const proposalId = pane.data('proposal-id');
            const tab = $(`.pricing-proposal-tab[data-proposal-id="${proposalId}"]`);
            const value = $(this).val().trim();
            tab.find('.pricing-proposal-tab-name').text(value || tab.data('fallback-label'));
        });
    },

    // --- Add / remove proposals (up to 3), primary designation + auto-promotion on delete ---
    updateAddProposalButtonState() {
        const count = $('.pricing-proposal-tabs').children('.nav-item').length;
        $('#add-proposal-btn').prop('disabled', count >= MAX_PROPOSALS);
    },

    bindAddProposal() {
        const self = this;
        $('#add-proposal-btn').on('click', function () {
            const container = $('#pricing-proposals-container');
            if (container.find('.pricing-proposal-tabs').children('.nav-item').length >= MAX_PROPOSALS) return;

            $.ajax({
                url: container.data('create-url'),
                method: 'POST',
                data: { deal_pricing_request_id: self.dealPricingRequestId },
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            }).done((response) => {
                if (!response.id) {
                    alert(response.err_msg);
                    return;
                }

                const pane = $('.pricing-proposal-form-template .pricing-proposal-pane').clone();
                pane.attr('data-proposal-id', response.id);
                pane.find('.pricing-proposal-card').attr('data-proposal-id', response.id);
                pane.find('.pricing-proposal-card').removeClass('pricing-proposal-primary');
                pane.find('input, textarea, select').val('');
                pane.find('[data-price-id]').removeAttr('data-price-id').removeData('price-id');

                // Map each cloned SKU row to the real DealPricingRequestProposalSku id just vivified
                // server-side for this new proposal, keyed by the buyer SKU id both sides already agree on.
                (response.proposal_skus || []).forEach((proposalSku) => {
                    pane.find(`.pricing-sku-row[data-sku-id="${proposalSku.deal_pricing_request_sku_id}"]`)
                        .attr('data-proposal-sku-id', proposalSku.id)
                        .data('proposal-sku-id', proposalSku.id);
                });

                const tabItem = $('.pricing-proposal-tab-template').children('.nav-item').clone();
                tabItem.find('.pricing-proposal-tab').attr('data-proposal-id', response.id);

                $('.pricing-proposal-tabs').append(tabItem);
                $('.pricing-proposal-panes').append(pane);
                self.activateTab(response.id);
                self.updateAddProposalButtonState();
            });
        });
    },

    bindRemoveProposal() {
        const self = this;
        $('#pricing-proposals-container').on('click', '.remove-proposal-btn', function () {
            const pane = $(this).closest('.pricing-proposal-pane');
            const proposalId = pane.data('proposal-id');
            const tab = $(`.pricing-proposal-tab[data-proposal-id="${proposalId}"]`);
            const wasActive = tab.hasClass('active');

            const removeLocally = () => {
                pane.remove();
                tab.closest('.nav-item').remove();
                self.updateAddProposalButtonState();

                if (wasActive) {
                    const firstTab = $('.pricing-proposal-tabs .pricing-proposal-tab').first();
                    if (firstTab.length) self.activateTab(firstTab.data('proposal-id'));
                }
            };

            if (!proposalId) {
                removeLocally();
                return;
            }

            $.ajax({
                url: `/deal_pricing_request_proposals/${proposalId}`,
                method: 'DELETE',
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            }).done((response) => {
                removeLocally();

                if (response && response.promoted_proposal_id) {
                    self.markProposalPrimary(response.promoted_proposal_id);
                    self.showAutoPromotionAlert(response.promoted_proposal_id);
                }
            });
        });
    },

    markProposalPrimary(proposalId) {
        const primaryLabel = window.dealPricingRequestProposalPrimaryLabel || 'Primary Proposal';
        const setPrimaryLabel = window.dealPricingRequestProposalSetPrimaryLabel || 'Set as Primary Proposal';

        const cards = $('#pricing-proposals-container .pricing-proposal-card');
        cards.removeClass('pricing-proposal-primary')
            .find('.mark-primary-btn').prop('disabled', false).removeClass('primary-button').addClass('secondary-button').text(setPrimaryLabel);

        const promotedCard = cards.filter(`[data-proposal-id="${proposalId}"]`);
        promotedCard.addClass('pricing-proposal-primary');
        promotedCard.find('.mark-primary-btn').prop('disabled', true).removeClass('secondary-button').addClass('primary-button').text(primaryLabel);

        $('.pricing-proposal-tabs .pricing-proposal-tab-star').addClass('d-none');
        $(`.pricing-proposal-tabs .pricing-proposal-tab[data-proposal-id="${proposalId}"] .pricing-proposal-tab-star`).removeClass('d-none');
    },

    showAutoPromotionAlert(proposalId) {
        const promotedCard = $(`#pricing-proposals-container .pricing-proposal-card[data-proposal-id="${proposalId}"]`);
        const name = promotedCard.find('input[name="name"]').val() || promotedCard.data('proposal-id');
        const template = window.dealPricingRequestProposalPromotedMessage || '%{name} has been automatically made the primary proposal.';

        alert(template.replace('%{name}', name));
    },

    bindPrimaryToggle() {
        const self = this;
        $('#pricing-proposals-container').on('click', '.mark-primary-btn', function () {
            if ($(this).prop('disabled')) return;

            const card = $(this).closest('.pricing-proposal-card');
            const proposalId = card.data('proposal-id');
            if (!proposalId) return;

            $.ajax({
                url: `/deal_pricing_request_proposals/${proposalId}`,
                method: 'PATCH',
                data: { is_primary: true },
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            }).done(() => self.markProposalPrimary(proposalId));
        });
    },

    // --- "Can you meet these packaging requirements?" Yes/No toggle — mirrors the mark-primary-btn
    // pattern (two plain buttons, not a native input) but routes its value through the existing
    // hidden-input `.autosave-proposal-field` so it reuses bindProposalFieldAutosave's PATCH logic
    // rather than needing its own AJAX call.
    bindPackagingRequirementsToggle() {
        $('#pricing-proposals-container').on('click', '.packaging-requirements-btn', function () {
            const button = $(this);
            const card = button.closest('.pricing-proposal-card');
            const value = button.data('value');

            card.find('.packaging-requirements-btn').removeClass('primary-button').addClass('secondary-button');
            button.removeClass('secondary-button').addClass('primary-button');

            card.find('input[name="can_meet_packaging_requirements"]').val(value).trigger('change');
        });
    },

    // --- "Are these terms acceptable?" Yes/No toggles (Payment Terms, Contract Length, Launch
    // Timeframe) — generalizes the packaging-requirements-btn pattern above via data-target/
    // data-value so one binder covers every field's hidden autosave input, plus optionally
    // revealing a `.terms-counter-suggestion[data-for]` row when the answer is "No".
    bindTermsAcceptToggle() {
        $('#pricing-proposals-container').on('click', '.terms-accept-btn', function () {
            const button = $(this);
            const card = button.closest('.pricing-proposal-card');
            const target = button.data('target');
            const value = button.data('value');

            card.find(`.terms-accept-btn[data-target="${target}"]`).removeClass('primary-button').addClass('secondary-button');
            button.removeClass('secondary-button').addClass('primary-button');

            card.find(`input[name="${target}"]`).val(value).trigger('change');
            card.find(`.terms-counter-suggestion[data-for="${target}"]`).toggleClass('d-none', value !== false);
        });
    },

    // --- "Can you meet these logistics requirements?" Yes/No toggle — unlike bindTermsAcceptToggle,
    // the fields stay visible for BOTH answers (only the red counter-suggestion hint is answer-gated),
    // and the first answer given also pre-fills the buyer's requested values via each field's
    // data-buyer-value attribute (set from the corresponding deal_pricing_request column in the view).
    bindLogisticsAcceptToggle() {
        $('#pricing-proposals-container').on('click', '.logistics-accept-btn', function () {
            const button = $(this);
            const card = button.closest('.pricing-proposal-card');
            const value = button.data('value');

            card.find('.logistics-accept-btn').removeClass('primary-button').addClass('secondary-button');
            button.removeClass('secondary-button').addClass('primary-button');

            card.find('input[name="can_meet_logistics_requirements"]').val(value).trigger('change');
            card.find('.logistics-alternative-hint').toggleClass('d-none', value !== false);

            const fieldsContainer = card.find('.logistics-fields');
            fieldsContainer.removeClass('d-none');
            fieldsContainer.find('.autosave-proposal-field').each(function () {
                const field = $(this);
                field.val(value ? (field.data('buyer-value') ?? '') : '').trigger('change');
            });
        });
    },

    // --- "Common option, or your own text" selects (Payment Terms / Contract Length counter-
    // suggestions) — mirrors the buyer-side wizard's identical control (create_supply_task_requests.js),
    // but here the resolved value is written into a hidden `.autosave-proposal-field` so it flows
    // through the existing per-field PATCH autosave instead of a page-wide submit.
    bindSelectWithOtherToggle() {
        const self = this;
        $('#pricing-proposals-container').on('change', '.select-with-other-wrapper .select-with-other', function () {
            const wrapper = $(this).closest('.select-with-other-wrapper');
            wrapper.find('.other-input').toggleClass('d-none', $(this).val() !== 'other');
            self.syncSelectWithOtherValue(wrapper);
        });

        $('#pricing-proposals-container').on('blur change', '.select-with-other-wrapper .other-input', function () {
            self.syncSelectWithOtherValue($(this).closest('.select-with-other-wrapper'));
        });
    },

    syncSelectWithOtherValue(wrapper) {
        const select = wrapper.find('.select-with-other');
        const otherInput = wrapper.find('.other-input');
        const target = wrapper.data('autosave-target');
        const resolved = select.val() === 'other' ? otherInput.val() : select.val();

        wrapper.find(`input[name="${target}"]`).val(resolved).trigger('change');
    },

    // --- Save as Draft / Submit Pricing ---
    bindSaveAsDraft() {
        $('#save-as-draft-btn').on('click', () => {
            // Every field already autosaves as it's entered (FR-015) — this just flushes any pending
            // debounced calls immediately and waits for them before confirming the draft is saved.
            this.flushPendingAutosaves().always(() => {
                $('#save-draft-confirmation').stop(true).show().delay(3000).fadeOut();
            });
        });
    },

    // The form has a real `type="submit"` button (#submit-pricing-btn) elsewhere in it, so per the
    // HTML spec, Enter in any single-line text input submits the form — but suppliers routinely hit
    // Enter just to move between price/notes/company-detail fields, which would fire the same
    // "Submit Pricing" request as clicking the button. <textarea> and the buttons themselves are left
    // free to handle Enter/click as normal.
    bindPreventEnterSubmit() {
        $('#pricing-proposal-entry-form').on('keydown', 'input', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        });
    },

    bindSubmitPricing() {
        $('#pricing-proposal-entry-form').on('submit', (event) => {
            event.preventDefault();

            // Wait for any still-debounced field to actually save before submitting — otherwise the
            // submit_pricing validation could run against a stale value for whatever was just typed.
            this.flushPendingAutosaves().always(() => {
                $.ajax({
                    url: `/deal_pricing_requests/${this.dealPricingRequestId}`,
                    method: 'PATCH',
                    dataType: 'json',
                    data: { deal_pricing_request: { status: 'pricing_provided' } },
                    headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
                }).always((response) => {
                    // The endpoint always responds 200 whether submission succeeded or the deferred
                    // validation (FR-011) rejected it, so success/failure comes from the JSON body, not
                    // the HTTP status. On failure, land back on this entry page (with its flash error)
                    // instead of the show page, so the supplier can see and fix what's wrong.
                    const succeeded = response && response.success;
                    window.location.href = succeeded
                        ? `/deal_pricing_requests/${this.dealPricingRequestId}`
                        : `/deal_pricing_requests/${this.dealPricingRequestId}/add_additional_information`;
                });
            });
        });
    },

    // --- Attachment upload (mirrors UpdatePricingRequest.initDropZone, adapted to this page's ids) ---
    initDropZone() {
        const submitButton = document.getElementById('submit-pricing-btn');
        const self = this;

        $('.add-pricing-request-btn').not('.dz-clickable').each((index, element) => {
            $(element).dropzone({
                url: '/deal_pricing_requests/upload_deal_pricing_request_attachment',
                previewsContainer: $(element).siblings('.drop-zone-preview')[0],
                previewTemplate: document.querySelector('.drop-zone-custom').innerHTML,
                uploadMultiple: false,
                maxFiles: 10,
                maxFileSize: 10,
                acceptedFiles: '.jpg,.jpeg,.gif,.png,.tiff,.bmp,.webp,.txt,.csv,.pdf,.xls,.xlsx,.xlsm,.doc,.docx,.zip',
                init() {
                    this.on('error', (file) => {
                        this.removeFile(file);
                        if (submitButton) submitButton.disabled = false;
                    });
                },
                params: { authenticity_token: ENTRY_AUTH_TOKEN },
                timeout: 60000,
                success: (file, response) => {
                    if (!response.err_msg) {
                        $(file.previewElement).find('.remove-attachment').attr('data-attachment-id', response.id);
                        $('#deal-pricing-request-attachments').append(
                            `<input type='hidden' value='${response.id}' name='deal_pricing_request[deal_pricing_request_attachment_ids][]'>`
                        );
                        self.bindRemoveAttachment();
                        self.autosaveAttachment(response.id);
                    } else {
                        alert(response.err_msg);
                        file.previewElement.remove();
                    }
                    if (submitButton) submitButton.disabled = false;
                },
                sending: () => {
                    if (submitButton) submitButton.disabled = true;
                }
            });
        });
    },

    bindRemoveAttachment() {
        const self = this;
        $('.remove-attachment').off('click').on('click', function (event) {
            event.preventDefault();
            const attachmentId = $(this).data('attachment-id');
            const preview = $(this).closest('.dz-preview');

            $.ajax({
                url: `/deal_pricing_requests/${self.dealPricingRequestId}/attachment/${attachmentId}`,
                method: 'DELETE',
                headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
            }).always(() => {
                // Whether or not the attachment had already been unlinked server-side, the supplier
                // asked to remove it from their submission — reflect that in the UI either way.
                $(`#deal-pricing-request-attachments input[value="${attachmentId}"]`).remove();
                preview.remove();
            });
        });
    },

    // The hidden input added on upload only becomes part of a real request if the whole form is
    // submitted — this page never does that (every field autosaves on its own, and Submit Pricing only
    // PATCHes the status), so without this the attachment is created but never linked to the deal
    // pricing request. Sending just the new id is enough: SetDealPricingRequestAttachments only
    // appends for a non-empty array, it doesn't replace the existing set.
    autosaveAttachment(attachmentId) {
        $.ajax({
            url: `/deal_pricing_requests/${this.dealPricingRequestId}`,
            method: 'PATCH',
            data: { deal_pricing_request: { deal_pricing_request_attachment_ids: [attachmentId] } },
            headers: { 'X-CSRF-Token': ENTRY_AUTH_TOKEN }
        });
    }
};
